import { z } from 'zod';
import { LOCATION_TYPE_VALUES } from '@fas/types';
import {
  booleanQuerySchema,
  codeSchema,
  effectiveDatesShape,
  latitudeSchema,
  longitudeSchema,
  nonEmptyTrimmed,
  optionalText,
  paginationQuerySchema,
  refineEffectiveRange,
  timeZoneSchema,
  uuidSchema,
} from './common';

/*
 * Pattern used for every master-data entity:
 *   base (no defaults, so PATCH never resets omitted fields)
 *   create = base + refinements
 *   update = base.partial() + refinements
 */

// ── Institution ────────────────────────────────────────────────────────────────
const institutionBase = z.object({
  code: codeSchema(20),
  name: nonEmptyTrimmed(200, 'Name'),
  shortName: optionalText(50),
  timezone: timeZoneSchema,
  address: optionalText(500),
  currentTermId: uuidSchema.nullable().optional(),
  active: z.boolean().optional(),
});
export const createInstitutionSchema = institutionBase;
export const updateInstitutionSchema = institutionBase.partial();
export type CreateInstitutionInput = z.infer<typeof createInstitutionSchema>;
export type UpdateInstitutionInput = z.infer<typeof updateInstitutionSchema>;

// ── Campus ─────────────────────────────────────────────────────────────────────
const campusBase = z.object({
  institutionId: uuidSchema.optional(),
  code: codeSchema(20),
  name: nonEmptyTrimmed(200, 'Name'),
  address: optionalText(500),
  latitude: latitudeSchema.nullable().optional(),
  longitude: longitudeSchema.nullable().optional(),
  active: z.boolean().optional(),
  ...effectiveDatesShape,
});
export const createCampusSchema = campusBase.superRefine(refineEffectiveRange);
export const updateCampusSchema = campusBase.partial().superRefine(refineEffectiveRange);
export type CreateCampusInput = z.infer<typeof createCampusSchema>;
export type UpdateCampusInput = z.infer<typeof updateCampusSchema>;

// ── Building ───────────────────────────────────────────────────────────────────
const buildingBase = z.object({
  campusId: uuidSchema,
  code: codeSchema(20),
  name: nonEmptyTrimmed(200, 'Name'),
  active: z.boolean().optional(),
  ...effectiveDatesShape,
});
export const createBuildingSchema = buildingBase.superRefine(refineEffectiveRange);
export const updateBuildingSchema = buildingBase.partial().superRefine(refineEffectiveRange);
export type CreateBuildingInput = z.infer<typeof createBuildingSchema>;
export type UpdateBuildingInput = z.infer<typeof updateBuildingSchema>;

// ── College ────────────────────────────────────────────────────────────────────
const collegeBase = z.object({
  institutionId: uuidSchema.optional(),
  code: codeSchema(20),
  name: nonEmptyTrimmed(200, 'Name'),
  active: z.boolean().optional(),
  ...effectiveDatesShape,
});
export const createCollegeSchema = collegeBase.superRefine(refineEffectiveRange);
export const updateCollegeSchema = collegeBase.partial().superRefine(refineEffectiveRange);
export type CreateCollegeInput = z.infer<typeof createCollegeSchema>;
export type UpdateCollegeInput = z.infer<typeof updateCollegeSchema>;

// ── Department ─────────────────────────────────────────────────────────────────
const departmentBase = z.object({
  collegeId: uuidSchema,
  code: codeSchema(20),
  name: nonEmptyTrimmed(200, 'Name'),
  active: z.boolean().optional(),
  ...effectiveDatesShape,
});
export const createDepartmentSchema = departmentBase.superRefine(refineEffectiveRange);
export const updateDepartmentSchema = departmentBase.partial().superRefine(refineEffectiveRange);
export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>;
export type UpdateDepartmentInput = z.infer<typeof updateDepartmentSchema>;

// ── Program ────────────────────────────────────────────────────────────────────
const programBase = z.object({
  departmentId: uuidSchema,
  code: codeSchema(30),
  name: nonEmptyTrimmed(200, 'Name'),
  degreeLevel: optionalText(50),
  active: z.boolean().optional(),
  ...effectiveDatesShape,
});
export const createProgramSchema = programBase.superRefine(refineEffectiveRange);
export const updateProgramSchema = programBase.partial().superRefine(refineEffectiveRange);
export type CreateProgramInput = z.infer<typeof createProgramSchema>;
export type UpdateProgramInput = z.infer<typeof updateProgramSchema>;

// ── Location (rooms, laboratories, affiliate sites, approved remote sites) ─────
const locationBase = z.object({
  code: codeSchema(40),
  name: nonEmptyTrimmed(200, 'Name'),
  locationType: z.enum(LOCATION_TYPE_VALUES),
  campusId: uuidSchema.nullable().optional(),
  buildingId: uuidSchema.nullable().optional(),
  roomNumber: optionalText(40),
  capacity: z.number().int().min(0).max(100000).nullable().optional(),
  address: optionalText(500),
  latitude: latitudeSchema.nullable().optional(),
  longitude: longitudeSchema.nullable().optional(),
  remarks: optionalText(1000),
  active: z.boolean().optional(),
  ...effectiveDatesShape,
});

function refineLocation(
  v: { latitude?: number | null; longitude?: number | null; effectiveFrom?: string | null; effectiveTo?: string | null },
  ctx: z.RefinementCtx,
) {
  refineEffectiveRange(v, ctx);
  const hasLat = v.latitude !== undefined && v.latitude !== null;
  const hasLng = v.longitude !== undefined && v.longitude !== null;
  if (hasLat !== hasLng) {
    ctx.addIssue({
      code: 'custom',
      path: [hasLat ? 'longitude' : 'latitude'],
      message: 'Latitude and longitude must be provided together',
    });
  }
}
export const createLocationSchema = locationBase.superRefine(refineLocation);
export const updateLocationSchema = locationBase.partial().superRefine(refineLocation);
export type CreateLocationInput = z.infer<typeof createLocationSchema>;
export type UpdateLocationInput = z.infer<typeof updateLocationSchema>;

// ── List queries ───────────────────────────────────────────────────────────────
export const listMasterDataQuerySchema = paginationQuerySchema.extend({
  active: booleanQuerySchema.optional(),
});
export type ListMasterDataQuery = z.infer<typeof listMasterDataQuerySchema>;

export const listBuildingsQuerySchema = listMasterDataQuerySchema.extend({ campusId: uuidSchema.optional() });
export const listDepartmentsQuerySchema = listMasterDataQuerySchema.extend({ collegeId: uuidSchema.optional() });
export const listProgramsQuerySchema = listMasterDataQuerySchema.extend({
  departmentId: uuidSchema.optional(),
  collegeId: uuidSchema.optional(),
});
export const listLocationsQuerySchema = listMasterDataQuerySchema.extend({
  campusId: uuidSchema.optional(),
  buildingId: uuidSchema.optional(),
  locationType: z.enum(LOCATION_TYPE_VALUES).optional(),
});
